import type { ReactNode } from 'react';

/**
 * The read-only panel and the fact list inside it — the design system's card,
 * bound to its tokens.
 *
 * Promoted to the shared ring on its **third** surface: the account detail, the
 * report entry, and the case detail all draw a bordered surface holding a
 * definition list of facts, and all three read the same way because the design
 * system gives them one shape. The two earlier features re-export these under
 * the names their call sites already use, so the definition is single without a
 * sweep through every import.
 */
export function Panel({ children }: { children: ReactNode }) {
  return <div className="overflow-hidden rounded-md border border-border bg-surface">{children}</div>;
}

/**
 * A definition list of facts.
 *
 * The label column is sized to its content and the value column takes the rest,
 * so the two align down the list however long a label runs.
 *
 * **A `.mono` value goes on the inner element, never on the `<dd>`.** The class
 * carries `direction: ltr`, and on the cell itself it flips the whole grid track
 * — the value lands against the far edge instead of beside its own label.
 */
export function MetaGrid({ children }: { children: ReactNode }) {
  return (
    <dl className="m-0 grid gap-x-4 gap-y-2 text-sm [grid-template-columns:max-content_minmax(0,1fr)] [&_dd]:m-0 [&_dt]:text-ink-muted">
      {children}
    </dl>
  );
}
